/**
 * Small presentational pieces shared by every screen.
 *
 * Three of these carry rules rather than styling:
 *
 *   `Money` is the only way an amount reaches the screen. It takes a decimal
 *   string, formats it with exact arithmetic, and renders in tabular numerals.
 *   No screen formats an amount itself, so no screen can accidentally hand an
 *   amount to `Number()`.
 *
 *   `Badge` never states a status in colour alone. Every tone carries a glyph
 *   whose OUTLINE differs — disc, ring, diamond, triangle, dashed bar — so the
 *   states are still distinguishable with the colour removed. Red/green is the
 *   most common colour-vision deficiency and this panel releases real money.
 *
 *   `HoldDrift` renders realised hold against configured edge. It is the most
 *   valuable signal in the panel: sustained drift means a payout table is wrong
 *   or a game is being exploited. Its meter diverges from a centre baseline,
 *   because the DIRECTION of the drift is a different problem in each direction.
 */
import type { ReactNode } from 'react';
import { format, formatCurrency, formatPercent, sub, toUnits, cmp } from '../lib/money.ts';
import type { FormatOptions } from '../lib/money.ts';
import {
  IconAlert,
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconClock,
  IconCross,
  IconDash,
  IconDiamond,
  IconEmptyTray,
  IconPulse,
} from './icons.tsx';

// -------------------------------------------------------------------- money

interface MoneyProps extends FormatOptions {
  value: string;
  /** Prefix with the currency mark. Off inside dense columns. */
  currency?: boolean;
  /** Colour by sign — only where the sign carries meaning, e.g. a ledger. */
  colour?: boolean;
  className?: string;
}

export function Money({ value, currency, colour, className, ...options }: MoneyProps): ReactNode {
  const text = currency ? formatCurrency(value, options) : format(value, options);
  const sign = toUnits(value);
  const tone = !colour ? '' : sign < 0n ? ' stat__value--loss' : sign > 0n ? ' stat__value--win' : '';
  return <span className={`num${tone}${className ? ` ${className}` : ''}`}>{text}</span>;
}

// -------------------------------------------------------------------- badge

type Tone = 'neutral' | 'win' | 'pending' | 'loss' | 'info' | 'accent';

/**
 * One glyph per tone, and five different outlines between them. This mapping is
 * the accessibility contract — changing a tone's glyph to another tone's shape
 * defeats the point of having them.
 */
const TONE_GLYPH: Record<Tone, (props: { size?: 'sm' }) => ReactNode> = {
  win: (p) => <IconCheck {...p} />,
  pending: (p) => <IconClock {...p} />,
  info: (p) => <IconDiamond {...p} />,
  loss: (p) => <IconAlert {...p} />,
  neutral: (p) => <IconDash {...p} />,
  accent: (p) => <IconCheck {...p} />,
};

export function Badge({ tone, children }: { tone: Tone; children: ReactNode }): ReactNode {
  return (
    <span className={`badge badge--${tone}`}>
      {TONE_GLYPH[tone]({ size: 'sm' })}
      {children}
    </span>
  );
}

/**
 * Status colours are consistent across the whole panel, and the word is always
 * present — colour never carries meaning on its own.
 */
const STATUS_TONE: Record<string, Tone> = {
  // deposits
  pending: 'pending',
  confirmed: 'info',
  credited: 'win',
  failed: 'loss',
  expired: 'neutral',
  // withdrawals
  requested: 'pending',
  approved: 'info',
  paid: 'win',
  rejected: 'loss',
  // players
  active: 'win',
  suspended: 'loss',
  closed: 'neutral',
  // methods
  healthy: 'win',
  degraded: 'pending',
  down: 'loss',
};

/** A handful of statuses read better with a closing glyph than a severity one. */
const STATUS_GLYPH: Record<string, (props: { size?: 'sm' }) => ReactNode> = {
  rejected: (p) => <IconCross {...p} />,
  failed: (p) => <IconCross {...p} />,
};

export function StatusBadge({ status }: { status: string }): ReactNode {
  const tone = STATUS_TONE[status] ?? 'neutral';
  const glyph = STATUS_GLYPH[status];
  if (!glyph) return <Badge tone={tone}>{status}</Badge>;
  return (
    <span className={`badge badge--${tone}`}>
      {glyph({ size: 'sm' })}
      {status}
    </span>
  );
}

/** A standing fact rather than a control — access mode, data source, window. */
export function Chip({
  icon,
  tone,
  children,
}: {
  icon?: ReactNode;
  tone?: 'warn';
  children: ReactNode;
}): ReactNode {
  return (
    <span className={`chip${tone ? ` chip--${tone}` : ''}`}>
      {icon}
      {children}
    </span>
  );
}

// --------------------------------------------------------------- stat tiles

export function Stat({
  label,
  value,
  note,
  tone,
  title,
  icon,
  lead,
  trailing,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  /** `undefined` is permitted so a caller can compute the tone inline. */
  tone?: 'accent' | 'win' | 'loss' | 'pending' | undefined;
  title?: string;
  icon?: ReactNode;
  /** The one or two figures on a screen that decide what happens next. */
  lead?: boolean;
  /** A delta pill or meter that belongs to the figure. */
  trailing?: ReactNode;
}): ReactNode {
  return (
    <div className={`stat${lead ? ' stat--lead' : ''}`} title={title ?? ''}>
      <span className="stat__label">
        {icon}
        {label}
      </span>
      <span className={`stat__value${tone ? ` stat__value--${tone}` : ''}`}>{value}</span>
      {trailing ? <span className="stat__note">{trailing}</span> : null}
      {note ? <span className="stat__note">{note}</span> : null}
    </div>
  );
}

/** A signed figure attached to a stat: arrow, sign and colour for one fact. */
export function Delta({
  value,
  level,
}: {
  value: string;
  level: 'ok' | 'watch' | 'alert';
}): ReactNode {
  const negative = value.startsWith('-');
  return (
    <span className={`delta delta--${level}`}>
      {negative ? <IconArrowDown size="sm" /> : <IconArrowUp size="sm" />}
      {formatPercent(value, { signed: true })}
    </span>
  );
}

// --------------------------------------------------------------------- card

export function Card({
  title,
  sub,
  icon,
  count,
  actions,
  flush,
  feature,
  footer,
  children,
}: {
  title: string;
  /** One line saying what the operator is looking at. Feature cards only. */
  sub?: string;
  icon?: ReactNode;
  count?: number;
  actions?: ReactNode;
  flush?: boolean;
  /** Exactly one per screen. The reading the screen exists for. */
  feature?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className={`card${feature ? ' card--feature' : ''}`}>
      <header className="card__head">
        {icon ? <span className="card__icon">{icon}</span> : null}
        <span className="card__heading">
          <h2 className="card__title">{title}</h2>
          {sub ? <span className="card__sub">{sub}</span> : null}
        </span>
        {count === undefined ? null : (
          <span className="card__count">{count.toLocaleString()}</span>
        )}
        <span className="card__spacer" />
        {actions}
      </header>
      <div className={`card__body${flush ? ' card__body--flush' : ''}`}>{children}</div>
      {footer}
    </section>
  );
}

// ----------------------------------------------------------- state displays

/**
 * Empty, loading and error are three DIFFERENT designed states.
 *
 * A queue with nothing in it is good news and should look like a finished
 * screen; a request that failed is bad news and must not be mistakable for the
 * good news. The old panel rendered both as the same grey line of text, which
 * meant an operator could not tell a clear queue from a broken panel.
 */
export function EmptyState({
  title,
  hint,
  icon,
  compact,
  action,
}: {
  title: string;
  hint?: ReactNode;
  icon?: ReactNode;
  compact?: boolean;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className={`state${compact ? ' state--compact' : ''}`}>
      <span className="state__icon">{icon ?? <IconEmptyTray size="lg" />}</span>
      <span className="state__title">{title}</span>
      {hint ? <span className="state__hint">{hint}</span> : null}
      {action ? <span className="state__actions">{action}</span> : null}
    </div>
  );
}

export function ErrorState({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail?: string;
  onRetry?: () => void;
}): ReactNode {
  return (
    <div className="state state--error">
      <span className="state__icon">
        <IconAlert size="lg" />
      </span>
      <span className="state__title">{title}</span>
      {detail ? <span className="state__hint">{detail}</span> : null}
      {onRetry ? (
        <span className="state__actions">
          <button type="button" className="btn btn--sm btn--ghost" onClick={onRetry}>
            Try again
          </button>
        </span>
      ) : null}
    </div>
  );
}

/**
 * A loading table keeps the real geometry: the real column count at the real
 * row height, so nothing jumps when the data lands. `align` marks which columns
 * are numeric, because a right-aligned skeleton in a money column is the shape
 * the operator is about to read.
 */
export function TableSkeleton({
  columns,
  rows = 6,
}: {
  columns: readonly ('text' | 'num')[];
  rows?: number;
}): ReactNode {
  return (
    <div className="table-wrap">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {columns.map((kind, c) => (
                <td key={c} className={kind === 'num' ? 'right' : ''}>
                  <span
                    className={`skeleton ${kind === 'num' ? 'skeleton--narrow' : 'skeleton--wide'}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Kept for panels that only need a line of text inside a card body. */
export function Empty({ children }: { children: ReactNode }): ReactNode {
  return (
    <EmptyState compact title="Nothing to show" hint={children} icon={<IconEmptyTray size="lg" />} />
  );
}

/** A loading line for a small panel, where a full skeleton table is too much. */
export function Loading({ children }: { children?: ReactNode }): ReactNode {
  return (
    <EmptyState
      compact
      title="Loading"
      hint={children ?? 'Fetching from the server.'}
      icon={<IconPulse size="lg" />}
    />
  );
}

// --------------------------------------------------------------- hold drift

/**
 * Realised hold vs configured edge.
 *
 * Both figures are percentage strings; the drift is their exact difference in
 * percentage points, never a float subtraction. Thresholds:
 *
 *   under 0.50pp   normal variance — grey, no alarm
 *   0.50–1.00pp    worth watching — amber
 *   over 1.00pp    something is wrong — red, and it is stated in words
 *
 * A game with no turnover in the window shows a dash, not a zero: a hold
 * percentage over zero turnover is undefined and an operator must not read it
 * as "holding nothing".
 */
export const DRIFT_WATCH = '0.50';
export const DRIFT_ALERT = '1.00';

/** The meter's full scale, either side of the centre line. */
const DRIFT_FULL_SCALE = '2.00';

export function driftOf(realised: string | null, configured: string): string | null {
  if (realised === null) return null;
  return sub(realised, configured);
}

export function driftLevel(drift: string | null): 'none' | 'ok' | 'watch' | 'alert' {
  if (drift === null) return 'none';
  const magnitude = drift.startsWith('-') ? drift.slice(1) : drift;
  if (cmp(magnitude, DRIFT_ALERT) >= 0) return 'alert';
  if (cmp(magnitude, DRIFT_WATCH) >= 0) return 'watch';
  return 'ok';
}

/**
 * A percentage-point figure as a share of half the meter's width, 0–50.
 *
 * Computed in exact integer arithmetic like every other figure in this panel,
 * and converted to a number only once it is a plain 0–50 ratio for CSS — never
 * while it is still a reading. Half, because the meter diverges from a centre
 * baseline and each side owns half the track.
 */
function halfShare(magnitude: string): number {
  const share = (toUnits(magnitude) * 50n) / toUnits(DRIFT_FULL_SCALE);
  return Math.min(50, Number(share));
}

const DRIFT_GLYPH = {
  ok: <IconDash size="sm" />,
  watch: <IconClock size="sm" />,
  alert: <IconAlert size="sm" />,
} as const;

export function HoldDrift({
  realised,
  configured,
}: {
  realised: string | null;
  configured: string;
}): ReactNode {
  const drift = driftOf(realised, configured);
  const level = driftLevel(drift);

  if (drift === null) {
    return (
      <span className="drift">
        <span className="drift__value drift__value--ok">—</span>
      </span>
    );
  }

  const negative = drift.startsWith('-');
  const magnitude = negative ? drift.slice(1) : drift;
  const width = halfShare(magnitude);
  const alertOffset = halfShare(DRIFT_ALERT);
  const shown = level === 'none' ? 'ok' : level;

  return (
    <span className="drift">
      <span
        className="drift__meter"
        aria-hidden="true"
        title={`${formatPercent(drift, { signed: true })} against a full scale of ±${DRIFT_FULL_SCALE}pp`}
      >
        {/* One tick either side, at the alert threshold — the line that decides
            whether this is a shrug or a job. More ticks than that turns a
            reading into a ruler nobody reads. Then the centre baseline. */}
        <span className="drift__tick" style={{ left: `${50 - alertOffset}%` }} />
        <span className="drift__tick" style={{ left: `${50 + alertOffset}%` }} />
        <span className="drift__axis" style={{ left: '50%' }} />
        <span
          className={`drift__fill drift__fill--${shown}`}
          style={negative ? { right: '50%', width: `${width}%` } : { left: '50%', width: `${width}%` }}
        />
      </span>
      <span className={`drift__glyph--${shown}`}>{DRIFT_GLYPH[shown]}</span>
      <span className={`drift__value drift__value--${shown}`}>
        {formatPercent(drift, { signed: true })}
      </span>
    </span>
  );
}

/**
 * A single-direction meter for a quantity with no negative side — the payment
 * method usage bar. Same track, no centre axis, so the two are not confused.
 */
export function UsageMeter({
  percentage,
  level,
}: {
  percentage: string | null;
  level: 'ok' | 'watch' | 'alert';
}): ReactNode {
  const width =
    percentage === null
      ? 0
      : Math.min(100, Number((toUnits(percentage) * 100n) / toUnits('100')));

  return (
    <span className="meter">
      <span className="drift__meter" aria-hidden="true">
        <span
          className={`drift__fill drift__fill--${level}`}
          style={{ left: 0, width: `${width}%` }}
        />
      </span>
      <span className={`drift__value drift__value--${level}`}>
        {percentage === null ? '—' : `${percentage}%`}
      </span>
    </span>
  );
}
